"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex h-10 w-64 bg-muted/20 animate-pulse rounded-md" />;
  }
  return <NavigationMenu className="hidden lg:flex w-full justify-center">
      <NavigationMenuList className="gap-12">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium tracking-widest text-foreground/90 hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm lg:hidden animate-in fade-in slide-in-from-top-5 duration-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
        <span className="text-sm font-medium tracking-widest text-muted-foreground">MENU</span>
        <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-transparent">
          <X className="h-6 w-6" />
          <span className="sr-only">Close menu</span>
        </Button>
      </div>
      <nav className="flex-1 overflow-y-auto py-8 px-6">
        <ul className="flex flex-col gap-6">
          {menuItems.map((item, i) => <li key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block text-2xl font-light tracking-wide text-foreground hover:text-primary transition-colors" onClick={onClose}>
                {item.label}
              </Link>
            </li>)}
        </ul>
      </nav>
    </div>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={cn("fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border py-2 shadow-sm" : "bg-background py-4")}>
        <div className="container mx-auto px-6 md:px-8 grid grid-cols-2 items-center">
          {/* Left column — logo */}
          <Link to="/" className="text-xl md:text-3xl tracking-widest font-serif [font-variant:small-caps] text-foreground hover:text-primary transition-colors text-center leading-tight">
            Sharmat Psychiatric Group
          </Link>
        
          {/* Right column — nav + mobile toggle */}
          <div className="flex items-center justify-center gap-4 pl-8">
            <DesktopNav />
            <Button variant="ghost" size="icon" className="lg:hidden flex-shrink-0 text-foreground hover:bg-accent/50" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="h-7 w-7" />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>
      </header>

      <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>;
}
export default function Header() {
  return <MenuProvider menu_id="17" className="w-full">
      <HeaderContent />
    </MenuProvider>;
}